/** The itinerary-only words of a refused delete: the refusal codes' reasons, the batch
 *  delete's summaries and what depends on an itinerary. The placeholder words live with
 *  the catalog status words in `catalog/words.ts`. */
import type { ItineraryDeleteConflict, ItineraryDependents } from '@/api/itineraries';
import { PLACEHOLDER_LABEL } from '@/features/catalog/words';

/** Why the backend refused to delete an itinerary, by the DELETE's refusal code: the
 *  reason beside each id a refused batch delete lists. */
export const DELETE_REFUSAL_REASON: Readonly<Record<ItineraryDeleteConflict['code'], string>> = {
  itinerary_in_use: 'In use',
  itinerary_placeholder: PLACEHOLDER_LABEL,
};

/** What a batch delete's "started" snackbar adds when the backend left itineraries out:
 *  `n` of them, or null when the selection was also capped, so how many is unknown. */
export function batchDeleteLeftOut(n: number | null): string {
  return n === null
    ? 'Itineraries in use and placeholders are not deleted.'
    : `${n.toLocaleString('en-US')} not deleted — in use or a placeholder.`;
}

/** The title of a batch delete the backend refused for every selected itinerary. */
export function batchDeleteRefusedTitle(n: number): string {
  return `Can’t delete the selected ${n === 1 ? 'itinerary' : 'itineraries'}`;
}

/** What depends on an itinerary: a dependents count of the DELETE's 409. */
export type ItineraryDependentKey = Exclude<keyof ItineraryDependents, 'trips_added_capped'>;

type DependentWords = readonly [one: string, many: string, what: string];

/** [one, many, what it does] per dependents count. The backend's own words
 *  (`catalog_guards._DEPENDENT_PHRASES`), so a refused delete's list reads like its
 *  `detail`, and the itinerary record's Used by cell reads like both. */
const DEPENDENT_WORDS: Readonly<Record<ItineraryDependentKey, DependentWords>> = {
  tour_itineraries: ['tour', 'tours', 'built on it'],
  trips_added: ['trip', 'trips', 'added from it'],
  template_tours: ['tour', 'tours', 'using it as a template'],
  sharing_groups: ['other itinerary', 'other itineraries', 'sharing its days'],
  crosssell_tours: ['tour', 'tours', 'cross-selling it'],
};

/** Every dependents count the words cover, in the order the 409 lists them (the spec
 *  contract test pins it). */
export const DEPENDENT_COUNT_KEYS: readonly ItineraryDependentKey[] = [
  'tour_itineraries',
  'trips_added',
  'template_tours',
  'sharing_groups',
  'crosssell_tours',
];

/** One dependents count in words: "205 tours built on it", "1 tour cross-selling it",
 *  "1,000+ trips added from it" (`capped`: the backend stopped counting at `n`). */
export function dependentPhrase(key: ItineraryDependentKey, n: number, capped = false): string {
  const [one, many, what] = DEPENDENT_WORDS[key];
  return `${n.toLocaleString('en-US')}${capped ? '+' : ''} ${n === 1 && !capped ? one : many} ${what}`;
}

/** The non-zero dependents in plain words, in the 409's order. */
export function dependentPhrases(deps: ItineraryDependents): string[] {
  return DEPENDENT_COUNT_KEYS.flatMap((key) => {
    const n = deps[key];
    return n ? [dependentPhrase(key, n, key === 'trips_added' && deps.trips_added_capped)] : [];
  });
}
